import React from 'react';
import {
  Box,
  VStack,
  Text,
  Heading,
  Card,
  CardBody,
  useColorModeValue,
  Icon,
  Badge,
  HStack,
  Spacer
} from '@chakra-ui/react';
import { 
  FaTools,
  FaCheckCircle,
  FaTimesCircle,
  FaClock
} from 'react-icons/fa';

const FunctionCallMonitor = ({ 
  functionCalls = [], 
  availableFunctions = [] 
}) => {
  const cardBg = useColorModeValue('gray.50', 'gray.700');

  return (
    <Card bg={cardBg}>
      <CardBody>
        <VStack spacing={4} align="stretch">
          <HStack>
            <Heading size="md">Function Call Monitor</Heading>
            <Spacer />
            <Badge colorScheme="blue" fontSize="sm">
              {functionCalls.length} calls
            </Badge>
          </HStack>
          
          {functionCalls.length === 0 ? (
            <Box textAlign="center" py={8} color="gray.500">
              <Icon as={FaTools} boxSize={8} mb={2} />
              <Text fontSize="sm">No function calls yet</Text>
              <Text fontSize="xs">Functions will appear here when the AI calls them</Text>
            </Box>
          ) : (
            <VStack spacing={3} align="stretch" maxH="500px" overflowY="auto">
              {functionCalls.slice().reverse().map((call) => {
                const func = availableFunctions.find(f => f && f.name === call.name);
                return (
                  <Card key={call.id} bg="white" border="1px solid" borderColor="gray.200">
                    <CardBody p={3}>
                      <VStack align="start" spacing={2}>
                        <HStack justify="space-between" w="100%">
                          <HStack>
                            <Icon 
                              as={func?.icon || FaTools} 
                              color={`${func?.color || 'gray'}.500`} 
                              boxSize={4} 
                            />
                            <Text fontWeight="bold" fontSize="sm">{call.name || 'Unknown Function'}</Text>
                          </HStack>
                          <Badge 
                            colorScheme={
                              call.status === 'completed' ? 'green' : 
                              call.status === 'failed' ? 'red' : 
                              'blue'
                            }
                            size="sm"
                          >
                            <Icon 
                              as={
                                call.status === 'completed' ? FaCheckCircle : 
                                call.status === 'failed' ? FaTimesCircle : 
                                FaClock
                              } 
                              mr={1} 
                            />
                            {call.status || 'unknown'}
                          </Badge>
                        </HStack>
                        
                        <Text fontSize="xs" color="gray.600">
                          {call.timestamp ? call.timestamp.toLocaleTimeString() : 'No timestamp'}
                        </Text>
                        
                        <Box>
                          <Text fontSize="xs" color="gray.500" mb={1}>Parameters:</Text>
                          <Box p={2} bg="gray.50" borderRadius="md" fontSize="xs">
                            {call.arguments && typeof call.arguments === 'object' ? (
                              Object.entries(call.arguments).map(([key, value]) => (
                                <Text key={key}>
                                  <Text as="span" fontWeight="bold">{key}:</Text> {String(value)}
                                </Text>
                              ))
                            ) : (
                              <Text color="gray.500">No parameters</Text>
                            )}
                          </Box>
                        </Box>

                        {call.result && (
                          <Box>
                            <Text fontSize="xs" color="gray.500" mb={1}>Result:</Text>
                            <Box 
                              p={2} 
                              bg={call.result.success ? "green.50" : "red.50"} 
                              borderRadius="md" 
                              fontSize="xs"
                            >
                              <Text color={call.result.success ? "green.700" : "red.700"}>
                                {call.result.message || 'No message'}
                              </Text>
                              {call.result.data && (
                                <Box mt={1} fontSize="10px" color="gray.600">
                                  {JSON.stringify(call.result.data, null, 2)}
                                </Box>
                              )}
                            </Box>
                          </Box>
                        )}
                      </VStack>
                    </CardBody>
                  </Card>
                );
              })}
            </VStack>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
};

export default FunctionCallMonitor;